import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import FormLayout from "../layouts/FormLayout";
import { apiFetch, safeGoBack, fetchLunarForIntDate } from "../utils/wkCommon";
import { getUser } from "../utils/auth";
import { isZh } from "../utils/language";
import PreRegHeaderCard from "../components/preregentry/PreRegHeaderCard";
import PreRegParticipantSection from "../components/preregentry/PreRegParticipantSection";
import PreRegDynamicFields from "../components/preregentry/PreRegDynamicFields";
import PreRegConfirmSection from "../components/preregentry/PreRegConfirmSection";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PreRegFormEntry() {
  const history = useHistory();
  const location = useLocation();

  const rawQuery = location.search.replace("?", "");

  const formCode = rawQuery.split("&")[0];

  const isPIC = rawQuery.includes("&pic");
  const isBanshi = rawQuery.includes("&banshi");

  const formMode = isPIC
    ? "pic"
    : isBanshi
      ? "petugas"
      : "peserta";

  const defaultRole =
    formMode === "petugas"
      ? "petugas"
      : "peserta";

  const allowRoleSelection =
    formMode === "pic";

  const allowRegistrarSelection =
    formMode === "pic";

  const user = getUser();

  const [loading, setLoading] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  const [formConfig, setFormConfig] =
    useState(null);

  const [step, setStep] =
    useState(1);

	const [accessDenied, setAccessDenied] =
		useState(false);		
	
	const [lunarInfo, setLunarInfo] = useState(null);

  const [registrarType, setRegistrarType] =
    useState(
      user?.umat
        ? "internal"
        : "external"
    );

  const [registrationRole, setRegistrationRole] =
    useState(defaultRole);

  const [selectedUmat, setSelectedUmat] =
    useState(user?.umat || null);

  const [baseData, setBaseData] =
    useState({
      name: "",
      gender: "",
      age: "",
      occupation: "",
      education: "",
      fotang: "",
      address: "",
      phoneNumber: "",
      notes: "",
      email: "",
    });

  const [customValues, setCustomValues] =
    useState({});

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {

      const url =
        `/api/agenda-pre-reg-forms?` +
        `filters%5BpreRegFormCode%5D%5B$eq%5D=${encodeURIComponent(formCode)}` +
        `&populate=*`;

      const resp = await apiFetch(url);

      const item = resp?.data?.[0];

      if (!item) {
        alert(
          isZh()
            ? "找不到表單"
            : "Form tidak ditemukan"
        );

        safeGoBack(history, "/");

        return;
      }

      setFormConfig(item);
			const cfg = item?.preRegCustomFields || {};

			const scope = cfg?.formScope || "internal_external";

			const loggedIn = !!user?.umat;

			if (scope === "internal" && !loggedIn) {
				setFormConfig(item);
				setAccessDenied(true);
				setLoading(false);
				return;
			}

			if (scope === "internal_external" && registrarType === "public") {
				setRegistrarType("external");
			}

			if (scope === "internal_public" && registrarType === "external") {
				setRegistrarType("public");
			}

      if (
        user?.umat &&
        !allowRegistrarSelection
      ) {
        fillFromUmat(user.umat);
      }

      setLoading(false);

    } catch (e) {

      console.error(e);

      alert(
        isZh()
          ? "載入失敗"
          : "Gagal load form"
      );
    }
  }

  function fillFromUmat(umat) {
    if (!umat) return;

    setSelectedUmat(umat);

    setBaseData({
      name:
        umat.fullName ||
        umat.displayName ||
        "",
      gender:
        umat.gender || "",
      age: "",
      occupation:
        umat.occupation || "",
      education:
        umat.education || "",
      fotang: "",
      address:
        umat.address || "",
      phoneNumber:
        umat.mobilePhone || "",
      notes: "",
    });
  }

	const agendaDate = formConfig?.preRegCustomFields?.agendaDate;

	useEffect(() => {
		let mounted = true;
		if (agendaDate) {
			fetchLunarForIntDate(agendaDate)
				.then((res) => {
					if (mounted) {
						setLunarInfo(res);
					}
				})
				.catch(() => {

					if (mounted) {
						setLunarInfo(null);
					}
				});
		} else {
			setLunarInfo(null);
		}

		return () => {
			mounted = false;
		};
	}, [agendaDate]);

  const fields = useMemo(() => {
    return (
      formConfig?.preRegCustomFields?.fields ||
      []
    );
  }, [formConfig]);

  function validateStep1() {

		if (registrarType === "internal" && !selectedUmat) {
			alert(isZh() ? "請選擇會員" : "Harap pilih umat");
			return false;
		}

    if (!baseData.name) {
      alert(
        isZh()
          ? "請填寫姓名"
          : "Nama wajib diisi"
      );

      return false;
    }

    if (!baseData.phoneNumber) {
      alert(
        isZh()
          ? "請填寫電話"
          : "No. Telp wajib diisi"
      );

      return false;
    }

    return true;
  }

	function validateStep2() {

		for (const f of fields) {

			const value =
				customValues?.[f.id];

			const isEmpty =
				value === undefined ||
				value === null ||
				value === "" ||
				(
					Array.isArray(value) &&
					value.length === 0
				);

			if (
				f.required === true &&
				isEmpty
			) {

				const label =
					isZh()
						? (
							f.labelZh ||
							f.labelId ||
							"欄位"
						)
						: (
							f.labelId ||
							f.labelZh ||
							"Field"
						);

				alert(
					isZh()
						? `請填寫必填欄位：${label}`
						: `Harap isi field wajib: ${label}`
				);

				return false;
			}
		}

		return true;
	}

  async function handleSubmit() {

    if (!validateStep1()) return;

    if (!validateStep2()) return;

    try {

      setSubmitting(true);

      const payload = {
				agenda_pre_reg_form: {
					connect: [
						formConfig.documentId
					]
				},
				agendaDate:
					formConfig?.preRegCustomFields?.agendaDate ||
					null,
				reg_status:
          "submitted",
				processedAt: null,
        personType:
          registrarType === "internal"
            ? "Internal"
            : registrarType === "external"
              ? "External"
              : "Public",
        registrationRole:
					registrationRole === "petugas"
						? "Petugas"
						: "Peserta",
        occupation:
          baseData.occupation,
        education:
          baseData.education,
        fotang_external:
          baseData.fotang,
        PreRegistrationCustomFieldValues:
          customValues,
				name:
					registrarType === "public"
						? baseData.nonUmatName
						: baseData.name,
				gender:
					registrarType === "public"
						? baseData.nonUmatGender
						: baseData.gender,
				age:
					registrarType === "public"
						? (
								baseData.nonUmatAge ||
								undefined
							)
						: (
								baseData.age ||
								undefined
							),
				phoneNumber:
					registrarType === "public"
						? baseData.nonUmatPhoneNo
						: baseData.phoneNumber,
				notes:
					registrarType === "public"
						? baseData.nonUmatRemarks
						: baseData.notes,
				address:
					registrarType === "public"
						? baseData.nonUmatAddress
						: baseData.address,					
      };

      if (selectedUmat?.documentId) {

        payload.umat_documentId =
          selectedUmat.documentId;
      }

      if (user?.username) {

        payload.submittedByUser =
          user.username;
      }

      const resp = await apiFetch(
        "/api/public-pre-registrations",
        {
          method: "POST",
          body: {
            data: payload,
          },
        }
      );

			alert(
				isZh()
					? (
						registrationRole === "petugas"
							? "辦事人員報名已成功提交，請等待後續確認。"
							: "報名已成功提交，請等待後續確認。"
					)
					: (
						registrationRole === "petugas"
							? "Pendaftaran petugas berhasil dikirim, silakan tunggu konfirmasi berikutnya."
							: "Pendaftaran berhasil dikirim, silakan tunggu konfirmasi berikutnya."
					)
			);
			setStep(4);

    } catch (e) {

      console.error(
				"SUBMIT_ERROR",
				e?.response?.data || e
			);

      alert(
        isZh()
          ? "提交失敗"
          : "Gagal submit"
      );

    } finally {

      setSubmitting(false);
    }
  }

  if (loading) return null;

	if (accessDenied) {
		return (
			<FormLayout
				hideTopButton
				hideBottomButton
				hideBackButton
				title={
					isZh()
						? "報名表"
						: "Form Pendaftaran"
				}
				leftPanelNode={
					<PreRegHeaderCard
						registrarType="public"
						formConfig={formConfig}
						lunarInfo={lunarInfo}
					/>
				}
			>
				<div className="access-denied-card">
					<div className="access-denied-title">
						此表單僅限內部會員
					</div>
					<div className="access-denied-desc">
						您必須先登入內部會員帳號才能填寫此報名表。
					</div>
					<div className="access-divider" />
					<div className="access-denied-title id">
						Form ini hanya untuk internal
					</div>
					<div className="access-denied-desc">
						Anda harus login sebagai umat internal terlebih dahulu untuk mengisi form ini.
					</div>
				</div>
				<style>{`
					.access-denied-card{
						background:#fff;
						border:1px solid #fecaca;
						border-radius:1.2rem;
						padding:2rem;
					}

					.access-denied-title{
						font-size:1.2rem;
						font-weight:700;
						color:#991b1b;
					}

					.access-denied-title.id{
						margin-top:1.5rem;
					}

					.access-denied-desc{
						margin-top:0.6rem;
						font-size:0.95rem;
						line-height:1.7;
						color:#475569;
					}

					.access-divider{
						margin-top:1.5rem;
						border-top:1px solid #e2e8f0;
					}
				`}</style>
			</FormLayout>
		);
	}

	const roleLabel =
		registrationRole === "petugas"
			? (
				isZh()
					? "辦事人員"
					: "Petugas"
			)
			: (
				isZh()
					? "班員"
					: "Peserta"
			);

	return (
		<FormLayout
			hideTopButton
			hideBottomButton
			hideBackButton={
				!(
					formMode === "pic" ||
					registrarType === "internal"
				)
			}

			headerLeftNode={
				formMode === "pic" ||
				registrarType === "internal"
					? undefined
					: <div />
			}

			title={
				isZh()
					? (
						formConfig?.titleZh ||
						formConfig?.titleId
					)
					: (
						formConfig?.titleId ||
						formConfig?.titleZh
					)
			}

			leftPanelNode={
				<PreRegHeaderCard
					registrarType={registrarType}
					formConfig={formConfig}
					lunarInfo={lunarInfo}
				/>
			}
		>

			{step === 1 && (
				<PreRegParticipantSection

					formMode={formMode}

					allowRoleSelection={
						allowRoleSelection
					}

					allowRegistrarSelection={
						allowRegistrarSelection
					}

					step={step}
					setStep={setStep}

					registrarType={registrarType}
					setRegistrarType={setRegistrarType}

					registrationRole={registrationRole}
					setRegistrationRole={setRegistrationRole}

					selectedUmat={selectedUmat}
					setSelectedUmat={setSelectedUmat}

					fillFromUmat={fillFromUmat}

					baseData={baseData}
					setBaseData={setBaseData}

					formConfig={formConfig}
				/>
			)}

			{step === 2 && (
				<PreRegDynamicFields
					fields={fields}
					customValues={customValues}
					setCustomValues={setCustomValues}
					setStep={setStep}
					registrationRole={registrationRole}
					registrarType={registrarType}
					allowRegistrarSelection={
						allowRegistrarSelection
					}
				/>
			)}

			{step === 3 && (
				<PreRegConfirmSection
					baseData={baseData}
					registrationRole={registrationRole}
					setStep={setStep}
					submitting={submitting}

					registrarType={registrarType}

					allowRegistrarSelection={
						allowRegistrarSelection
					}
					handleSubmit={handleSubmit}
					fields={fields}
					customValues={customValues}
				/>
			)}

			{step === 4 && (

				<div className="success-card">

					<div className="success-icon">
						✓
					</div>

					<div className="success-title">

						{isZh()
							? "提交成功"
							: "Pendaftaran Berhasil"}

					</div>

					<div className="success-desc">

						{isZh()
							? (
								registrationRole === "petugas"
									? "辦事人員報名已成功提交，請等待後續確認。"
									: "報名資料已成功提交，請等待後續確認。"
							)
							: (
								registrationRole === "petugas"
									? "Pendaftaran petugas berhasil dikirim. Silakan tunggu konfirmasi berikutnya."
									: "Data pendaftaran berhasil dikirim. Silakan tunggu konfirmasi berikutnya."
							)}

					</div>

					<div className="success-meta">

						<div>
							<b>
								{isZh()
									? "姓名"
									: "Nama"}
							</b>
						</div>

						<div>
							{baseData.name}
						</div>

					</div>

					<div className="success-meta">

						<div>
							<b>
								{isZh()
									? "身份"
									: "Role"}
							</b>
						</div>

						<div>
							{roleLabel}
						</div>

					</div>

				</div>
			)}

			<style>{`

				.mobile-summary-wrap{
					margin-bottom:1rem;
				}

				.mobile-summary-toggle{
					width:100%;
					display:flex;
					align-items:center;
					justify-content:space-between;
					border:1px solid #dbe3ef;
					background:#fff;
					border-radius:0.8rem;
					padding:0.9rem 1rem;
					font-size:0.95rem;
					font-weight:700;
					cursor:pointer;
				}

				.mobile-summary-body{
					margin-top:0.8rem;
				}

				.success-card{
					background:#fff;
					border:1px solid #dbe3ef;
					border-radius:1.2rem;
					padding:2rem;
					text-align:center;
				}

				.success-icon{
					width:4rem;
					height:4rem;
					border-radius:999rem;
					background:#dcfce7;
					color:#166534;
					font-size:2rem;
					font-weight:700;
					display:flex;
					align-items:center;
					justify-content:center;
					margin:0 auto 1rem;
				}

				.success-title{
					font-size:1.3rem;
					font-weight:700;
				}

				.success-desc{
					margin-top:0.8rem;
					font-size:0.95rem;
					color:#475569;
					line-height:1.6;
				}

				.success-meta{
					margin-top:1rem;
					padding-top:1rem;
					border-top:1px solid #e2e8f0;
				}

			`}</style>

		</FormLayout>
	);
}